import { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { 
  Home, Music, CalendarDays, Image, Menu, X,
  Instagram, Youtube, Twitter, Facebook, Play, Pause,
  SkipBack, SkipForward, Volume2, Heart, Headphones, Send, Mic2, Bell, BellOff, ShoppingBag
} from 'lucide-react';
import { usePlayer } from '../contexts/PlayerContext';
import { useFan } from '../contexts/FanContext';
import { usePageTracking } from '../hooks/useAnalytics';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/dj-shadow-logo.png";

const MainLayout = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [socialLinks, setSocialLinks] = useState({});
  const [showFanModal, setShowFanModal] = useState(false);
  const { fan, register, logout } = useFan();
  const { currentMix } = usePlayer();
  const { isSupported, isSubscribed, loading: pushLoading, subscribe, unsubscribe } = usePushNotifications();
  
  // Track page views
  usePageTracking();

  useEffect(() => {
    fetchSocialLinks();
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const fetchSocialLinks = async () => {
    try {
      const response = await axios.get(`${API_URL}/social-links`);
      setSocialLinks(response.data);
    } catch (error) {
      console.error('Failed to fetch social links:', error);
    }
  };

  const handleNotificationToggle = async () => {
    if (isSubscribed) {
      const success = await unsubscribe();
      if (success) {
        toast.success('Notifications turned off');
      }
    } else {
      const success = await subscribe();
      if (success) {
        toast.success('🔔 Notifications enabled! You\'ll be notified of new mixes.');
      } else {
        toast.error('Could not enable notifications. Please allow notifications in your browser settings.');
      }
    }
  };

  const navItems = [
    { icon: Home, label: 'Home', path: '/' },
    { icon: Music, label: 'Mixes', path: '/mixes' },
    { icon: CalendarDays, label: 'Events', path: '/events' },
    { icon: Image, label: 'Gallery', path: '/gallery' },
    { icon: Mic2, label: 'New Artists', path: '/new-artists' },
    { icon: ShoppingBag, label: 'Shop', path: '/shop' },
    { icon: Send, label: 'Book Now', path: '/bookings' },
  ];

  const socialIcons = {
    instagram: Instagram,
    youtube: Youtube,
    twitter: Twitter,
    facebook: Facebook,
  };

  return (
    <div className={`min-h-screen flex flex-col bg-[#09090B] noise-texture ${currentMix ? 'pb-24' : ''}`}>
      {/* NEON BACKGROUND EFFECTS */}
      <div className="grid-lines" />
      
      {/* Floating Neon Orbs */}
      <div className="neon-orb neon-orb-1" />
      <div className="neon-orb neon-orb-2" />
      <div className="neon-orb neon-orb-3" />
      
      {/* Laser Beams */}
      <div className="laser-beam laser-beam-1" />
      <div className="laser-beam laser-beam-2" />
      <div className="laser-beam laser-beam-3" />
      
      {/* Spotlight */}
      <div className="spotlight" />
      
      {/* Turntable Glows */}
      <div className="turntable-glow fixed -top-20 -left-20" />
      <div className="turntable-glow fixed -bottom-20 -right-20" style={{ animationDelay: '2s' }} />
      
      {/* Header - with safe area padding for mobile */}
      <header 
        className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10"
        style={{ 
          paddingTop: 'max(16px, env(safe-area-inset-top, 16px))',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between min-h-[56px]">
          {/* Logo */}
          <NavLink to="/" className="flex items-center gap-2 flex-shrink-0">
            <img 
              src={LOGO_URL}
              alt="Dj Shadow App Logo"
              className="h-8 md:h-10 w-auto rounded-lg"
            />
            <span className="font-bold text-base md:text-xl tracking-tight hidden sm:block">Dj Shadow App</span>
          </NavLink>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `
                  flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-200
                  ${isActive 
                    ? 'bg-[#D9F99D] text-black font-bold' 
                    : 'text-zinc-400 hover:text-white hover:bg-white/5'}
                `}
              >
                <item.icon className="w-4 h-4" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Right Side - Mobile Optimized */}
          <div className="flex items-center gap-1 md:gap-3 flex-shrink-0">
            {/* Notification Bell */}
            {isSupported && (
              <Button
                variant="ghost"
                onClick={handleNotificationToggle}
                disabled={pushLoading}
                className={`p-2 rounded-full transition-all min-w-[40px] min-h-[40px] ${
                  isSubscribed 
                    ? 'text-[#D9F99D] hover:bg-[#D9F99D]/10' 
                    : 'text-zinc-400 hover:text-white hover:bg-white/5'
                }`}
                title={isSubscribed ? 'Notifications On' : 'Enable Notifications'}
                data-testid="notification-bell"
              >
                {isSubscribed ? (
                  <Bell className="w-5 h-5" />
                ) : (
                  <BellOff className="w-5 h-5" />
                )}
              </Button>
            )}
            
            {fan ? (
              <Button 
                variant="ghost" 
                onClick={logout}
                className="text-zinc-400 hover:text-white text-sm hidden sm:flex"
              >
                Hi, {fan.name}
              </Button>
            ) : (
              <Button 
                onClick={() => setShowFanModal(true)}
                className="bg-[#D9F99D] text-black hover:bg-[#BEF264] rounded-full px-3 md:px-4 py-2 text-xs md:text-sm font-bold min-h-[40px]"
                data-testid="join-btn"
              >
                <Heart className="w-4 h-4 mr-1 md:mr-2" />
                <span className="hidden xs:inline">Join</span>
                <span className="xs:hidden">+</span>
              </Button>
            )}

            {/* Mobile Menu Button */}
            <Button 
              variant="ghost" 
              size="icon"
              className="md:hidden text-white min-w-[44px] min-h-[44px] p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <nav className="md:hidden border-t border-white/10 p-4 space-y-2 bg-[#09090B]">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `
                  flex items-center gap-3 px-4 py-3 rounded-xl transition-all
                  ${isActive 
                    ? 'bg-[#D9F99D] text-black font-bold' 
                    : 'text-zinc-400 hover:bg-white/5'}
                `}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      {/* Main Content - extra top padding for mobile safe area */}
      <main 
        className="flex-1" 
        style={{ 
          paddingTop: 'calc(80px + max(0px, env(safe-area-inset-top, 0px)))'
        }}
      >
        <Outlet />
      </main>

      {/* Player Bar */}
      <PlayerBar />

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 mt-auto">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src={LOGO_URL}
                alt="Dj Shadow App Logo"
                className="h-8 w-auto rounded-lg"
              />
              <span className="font-bold">Dj Shadow App</span>
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-4">
              {Object.entries(socialLinks).map(([platform, url]) => {
                if (!url) return null;
                const Icon = socialIcons[platform] || Instagram;
                return (
                  <a
                    key={platform}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors"
                  >
                    <Icon className="w-5 h-5 text-zinc-400" />
                  </a>
                );
              })}
            </div>

            <p className="text-zinc-500 text-sm">@IAMDISHADOW_NYC</p>
          </div>
        </div>
      </footer>

      {/* Fan Registration Modal */}
      <FanModal isOpen={showFanModal} onClose={() => setShowFanModal(false)} />
    </div>
  );
};

// Player Bar Component
const PlayerBar = () => {
  const { 
    currentMix, isPlaying, progress, volume,
    togglePlay, playNext, playPrevious, seekTo, setVolume,
    getCurrentTime, getDuration
  } = usePlayer();
  const { fan, toggleFavorite, isFavorite } = useFan();

  if (!currentMix) {
    return null;
  }

  const handleFavorite = async () => {
    if (!fan) {
      toast.error('Join to save favorites!');
      return;
    }
    await toggleFavorite(currentMix.id);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 glass border-t border-white/10 px-4 md:px-6 py-3" data-testid="player-bar">
      <div className="max-w-7xl mx-auto flex items-center gap-4">
        {/* Mix Info */}
        <div className="flex items-center gap-3 min-w-0 flex-1 md:flex-none md:w-64">
          <div className="w-12 h-12 rounded-lg overflow-hidden bg-zinc-900 flex-shrink-0">
            {currentMix.cover_url ? (
              <img src={currentMix.cover_url} alt={currentMix.title} className="w-full h-full object-contain" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Headphones className="w-6 h-6 text-zinc-600" />
              </div>
            )}
          </div>
          <div className="min-w-0">
            <p className="font-medium truncate text-sm" data-testid="player-mix-title">{currentMix.title}</p>
            <p className="text-xs text-zinc-400">DJ Shadow</p>
          </div>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={handleFavorite}
            className="hidden sm:flex"
          >
            <Heart className={`w-4 h-4 ${isFavorite(currentMix.id) ? 'fill-[#F472B6] text-[#F472B6]' : 'text-zinc-400'}`} />
          </Button>
        </div>

        {/* Controls */}
        <div className="flex-1 flex flex-col items-center justify-center max-w-xl">
          <div className="flex items-center gap-2 md:gap-4 mb-1">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={playPrevious}
              className="text-zinc-400 hover:text-white h-8 w-8"
            >
              <SkipBack className="w-4 h-4" />
            </Button>
            <Button 
              onClick={togglePlay}
              className="w-10 h-10 rounded-full bg-[#D9F99D] text-black hover:bg-[#BEF264]"
              data-testid="player-play-btn"
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={playNext}
              className="text-zinc-400 hover:text-white h-8 w-8"
            >
              <SkipForward className="w-4 h-4" />
            </Button>
          </div>

          <div className="w-full flex items-center gap-2">
            <span className="text-xs text-zinc-500 w-10 text-right font-mono hidden sm:block">{getCurrentTime()}</span>
            <Slider
              value={[progress]}
              max={100}
              step={0.1}
              onValueChange={([val]) => seekTo(val)}
              className="flex-1"
            />
            <span className="text-xs text-zinc-500 w-10 font-mono hidden sm:block">{getDuration()}</span>
          </div>
        </div>

        {/* Volume */}
        <div className="hidden md:flex items-center gap-2 w-32">
          <Volume2 className="w-4 h-4 text-zinc-400" />
          <Slider
            value={[volume * 100]}
            max={100}
            step={1}
            onValueChange={([val]) => setVolume(val / 100)}
            className="flex-1"
          />
        </div>
      </div>
    </div>
  );
};

// Fan Registration Modal
const FanModal = ({ isOpen, onClose }) => {
  const { register } = useFan();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      await register(formData.email, formData.name);
      toast.success('Welcome to the Shadow family!');
      onClose();
    } catch (error) {
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#18181B] border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-center">Join the Shadow Family</DialogTitle>
        </DialogHeader>
        <p className="text-zinc-400 text-center text-sm">
          Save your favorite mixes and get updates on new releases!
        </p>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="name">Your Name</Label>
            <Input
              id="name"
              placeholder="Enter your name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="bg-black/20 border-white/10 focus:border-[#D9F99D]"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="bg-black/20 border-white/10 focus:border-[#D9F99D]"
            />
          </div>
          <Button 
            type="submit"
            disabled={loading}
            className="w-full bg-[#D9F99D] text-black hover:bg-[#BEF264] rounded-full py-6 font-bold"
          >
            {loading ? 'Joining...' : 'Join Now'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MainLayout;
